import { useSession } from 'next-auth/react'
import { useState } from 'react'
import { trackFormSubmit } from '@utils/gtm'
import { trackRequestAQuoteModalInteraction } from './plusForm.utils'
import * as Sentry from '@sentry/nextjs'
import {
  CUSTOMER_TYPE_IN_COOKIE,
  getCookiePlusEditorFormId,
  getCookieRequestAQuoteFormId,
} from '@utils/constants'
import Cookie from 'js-cookie'
import {
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_COUNTRY,
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
} from '@utils/client-side-config'
import { signInWithKeycloack } from '@utils/authHelpers'
import { quotePayloadContainsSkus } from '@utils/hubspot'

export function useRequestAQuoteSubmission({
  formEvent,
  productSlug,
}: {
  formEvent: string
  productSlug: string
}) {
  const [formIsLoading, setFormIsLoading] = useState<boolean>(false)
  const [submissionError, setSubmissionError] = useState<string>()
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const { data: session } = useSession()
  const cookieFormPayloadId = getCookieRequestAQuoteFormId(productSlug)
  const cookiePlusEditorPayloadId = getCookiePlusEditorFormId(productSlug)

  const preSubmittedPayload = Cookie.get(cookieFormPayloadId) || '{}'
  const savedPlusEditorPayload = Cookie.get(cookiePlusEditorPayloadId) || '{}'

  /** Need to save RAQ payload with different IDs based on different flows to avoid colision. */
  const saveQuoteForDefaultRAQFlow = (quotePayload) => {
    Cookie.set(cookieFormPayloadId, JSON.stringify(quotePayload))
  }

  const savePayloadForPlusEditorFlow = (quotePayload) => {
    Cookie.set(cookiePlusEditorPayloadId, JSON.stringify(quotePayload))
  }

  const removeSavedRAQPayload = () => {
    Cookie.remove(cookiePlusEditorPayloadId)
    Cookie.remove(cookieFormPayloadId)
  }

  const submitForm = async (formPayload, productSlug) => {
    const customerType = Cookie.get(CUSTOMER_TYPE_IN_COOKIE)
    const currency = NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY
    const channel = NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_COUNTRY

    setFormIsLoading(true)
    setSubmissionError(null)
    try {
      const formData = {
        customerEmail: session?.email,
        eventName: formEvent,
        payload: { ...formPayload, customerType, currency, channel },
      }

      if (!quotePayloadContainsSkus(formPayload)) {
        const lineItems = formPayload?.fields?.find(
          (field) => field?.name === 'line_items'
        )?.value

        const productNames = lineItems
          ?.map((item) => {
            const itemName = item?.fields?.find(
              (x) => x?.name === 'product_name'
            )?.value

            return itemName
          })
          ?.filter(Boolean)

        Sentry.captureMessage(`RAQ form payload does not have a SKU.`, {
          level: 'warning',
          contexts: {
            Form: {
              productNames,
              customerEmail: formPayload?.customerEmail,
            },
          },
        })
      }

      await fetch(`/api/customer/generic-form/`, {
        method: 'POST',
        body: JSON.stringify(formData),
        headers: { 'Content-Type': 'application/json' },
      })
      trackFormSubmit({
        event: 'form-submit',
        formName: 'Quote request',
        eventStatus: 'complete',
      })
      removeSavedRAQPayload()

      setShowSuccessDialog(true)
      trackRequestAQuoteModalInteraction({
        componentValue: 'opened',
        productSlug,
      })
    } catch (err) {
      setSubmissionError(
        err.message || 'Something went wrong. Please try again.'
      )
      trackFormSubmit({
        event: 'form-submit',
        formName: 'Quote request',
        eventStatus: 'fail',
        failureReason: err.message,
      })

      Sentry.captureException(`RAQ request has failed. Error: ${err.message}`, {
        contexts: {
          formPayload,
          productSlug,
        },
      })
    } finally {
      setFormIsLoading(false)
    }
  }

  const handleUserLogin = async (redirectUrl = null) => {
    setFormIsLoading(true)
    setSubmissionError(null)
    try {
      // save form payload to cookies to submit the for on successful login
      await signInWithKeycloack({
        redirectRouteParameter: redirectUrl || window?.location?.pathname,
      })
    } catch (err) {
      setSubmissionError(
        err.message || 'Something went wrong. Please try again.'
      )
      setFormIsLoading(false)
    }
  }

  return {
    formIsLoading,
    setFormIsLoading,
    submissionError,
    setSubmissionError,
    submitForm,
    showSuccessDialog,
    setShowSuccessDialog,
    handleUserLogin,
    preSubmittedPayload,
    savedPlusEditorPayload,
    saveQuoteForDefaultRAQFlow,
    removeSavedRAQPayload,
    savePayloadForPlusEditorFlow,
  }
}
